import React, { useEffect, useState } from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { AlertCircle } from 'lucide-react';
import { useAuth } from '../context/AuthContext';

/**
 * Component to handle approval link authentication and redirect to ticket
 * This replaces the dedicated approval page to allow users to view the full ticket
 */
const ApprovalLinkHandler = () => {
  const { ticketId } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  const { loginWithApprovalToken, isAuthenticated } = useAuth();
  const [error, setError] = useState(null);
  const [authenticating, setAuthenticating] = useState(true);

  useEffect(() => {
    const handleApprovalLink = async () => {
      // Get approval token and optional redirect from URL parameters
      const urlParams = new URLSearchParams(location.search);
      const approvalToken = urlParams.get('approval_token');
      const redirectPath = urlParams.get('redirect');

      // Force log to console to ensure it's visible
      console.error('🔍 ApprovalLinkHandler - URL params:', {
        approvalToken: approvalToken ? 'present' : 'missing',
        redirectPath: redirectPath || 'none',
        ticketId,
        fullSearch: location.search
      });

      if (!approvalToken || !ticketId) {
        setError('Invalid approval link - missing token or ticket ID');
        setAuthenticating(false);
        return;
      }

      try {
        setAuthenticating(true);
        console.log('🔐 Processing approval link for ticket:', ticketId);
        if (redirectPath) {
          console.log('🔄 Custom redirect path:', redirectPath);
        }

        // Authenticate using the approval token
        const result = await loginWithApprovalToken(approvalToken, ticketId);

        if (result && result.status === 1) {
          console.error('✅ Approval token authentication successful, redirecting...');
          
          // If custom redirect is provided, use it; otherwise go to ticket
          if (redirectPath) {
            console.error('🔄 Using custom redirect:', redirectPath);
            navigate(redirectPath, { replace: true });
          } else if (ticketId === '999999') {
            // Special case for asset declarations without real tickets
            navigate('/dashboard/assets/declaration', { replace: true });
          } else {
            navigate(`/dashboard/helpdesk?ticket=${ticketId}`, { replace: true });
          }
        } else {
          setError('Authentication failed. The approval link may be invalid or expired.');
          setAuthenticating(false);
        }
      } catch (err) {
        console.error('❌ Approval link authentication error:', err);
        setError(err.message || 'Failed to authenticate. The approval link may be invalid or expired.');
        setAuthenticating(false);
      }
    };

    // Only proceed if not already authenticated
    if (!isAuthenticated) {
      handleApprovalLink();
    } else {
      // Already authenticated, check for custom redirect
      const urlParams = new URLSearchParams(location.search);
      const redirectPath = urlParams.get('redirect');
      
      console.error('✅ User already authenticated, redirecting...', { redirectPath });
      if (redirectPath) {
        navigate(redirectPath, { replace: true });
      } else if (ticketId === '999999') {
        // Special case for asset declarations without real tickets
        navigate('/dashboard/assets/declaration', { replace: true });
      } else {
        navigate(`/dashboard/helpdesk?ticket=${ticketId}`, { replace: true });
      }
    }
  }, [ticketId, location.search, loginWithApprovalToken, isAuthenticated, navigate]);

  if (authenticating) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-600 mx-auto mb-4"></div>
          <p className="text-gray-600">Authenticating and loading ticket...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gray-100 flex items-center justify-center">
        <div className="text-center max-w-md mx-auto p-6">
          <AlertCircle className="w-16 h-16 text-red-500 mx-auto mb-4" />
          <h2 className="text-xl font-semibold text-gray-900 mb-2">Authentication Error</h2>
          <p className="text-gray-600 mb-4">{error}</p>
          <button 
            onClick={() => navigate('/login')} 
            className="px-4 py-2 bg-blue-600 text-white rounded hover:bg-blue-700"
          >
            Go to Login
          </button>
        </div>
      </div>
    );
  }

  return null;
};

export default ApprovalLinkHandler;
